import { useParams, Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { getPostBySlug, BLOG_POSTS } from "@/data/blog-posts";

const PUBLIC_SITE_URL = "https://leydedatoschile.com";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const url = `${PUBLIC_SITE_URL}/blog/${post.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    image: post.cover ? `${PUBLIC_SITE_URL}${post.cover}` : undefined,
    author: { "@type": "Organization", name: "Ley de Datos Chile", url: PUBLIC_SITE_URL },
    publisher: {
      "@type": "Organization",
      name: "Ley de Datos Chile",
      logo: {
        "@type": "ImageObject",
        url: `${PUBLIC_SITE_URL}/favicon.svg`,
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    keywords: post.keywords,
    wordCount: post.content.split(/\s+/).length,
    articleBody: post.content.replace(/[#*`]/g, ""),
  };

  // Next/prev posts
  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const prev = BLOG_POSTS[idx - 1];
  const next = BLOG_POSTS[idx + 1];

  return (
    <>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        keywords={post.keywords}
        ogImage={post.cover ? `${PUBLIC_SITE_URL}${post.cover}` : undefined}
        schemas={[
          orgSchema,
          breadcrumbSchema([
            { name: "Inicio", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          articleSchema,
        ]}
      />
      <article className="px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <Link
            data-testid="back-to-blog"
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm font-mono uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900 mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Volver al blog
          </Link>

          {/* Breadcrumb visible — mejora SEO y UX */}
          <nav aria-label="Breadcrumb" className="mb-8" data-testid="post-breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500 font-mono tracking-[0.14em] uppercase">
              <li><Link to="/" className="hover:text-blue-700">Inicio</Link></li>
              <li aria-hidden="true">›</li>
              <li><Link to="/blog" className="hover:text-blue-700">Blog</Link></li>
              <li aria-hidden="true">›</li>
              <li className="text-slate-700 truncate max-w-[280px]" title={post.title}>{post.title}</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-6">
              <Calendar className="w-3 h-3" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" })}
              </time>
              <span className="text-slate-300">·</span>
              <Clock className="w-3 h-3" />
              <span aria-label={`Tiempo de lectura ${post.readingMinutes} minutos`}>{post.readingMinutes} min</span>
              <span className="text-slate-300">·</span>
              <span>{post.author}</span>
            </div>
            <h1
              data-testid="post-title"
              className="font-display text-4xl sm:text-5xl tracking-tight text-slate-900 leading-[1.05]"
            >
              {post.title}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              {post.excerpt}
            </p>
          </header>

          {post.cover && (
            <figure
              data-testid="post-cover"
              className="mb-12 -mx-2 sm:mx-0 overflow-hidden rounded-2xl bg-slate-100 aspect-[16/9] shadow-sm"
            >
              <img
                src={post.cover}
                alt={`Ilustración editorial · ${post.title} — Ley 21.719 Protección de Datos Personales Chile`}
                className="w-full h-full object-cover"
                loading="eager"
                width="1200"
                height="675"
              />
            </figure>
          )}

          <div
            data-testid="post-content"
            className="prose prose-slate prose-lg max-w-none prose-headings:font-display prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-p:leading-relaxed prose-a:text-blue-700 prose-a:font-medium prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 prose-li:my-1"
          >
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </div>

          {/* CTA bottom — dark Reaktor style */}
          <div className="mt-16 p-8 sm:p-10 rounded-2xl bg-ink-950 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
            <div className="relative">
              <p className="font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-3">
                Acción recomendada
              </p>
              <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
                ¿Tu PYME ya cumple la <em className="italic text-blue-300">Ley 21.719</em>?
              </h3>
              <p className="mt-3 text-slate-300 text-sm max-w-xl">
                Escanea tu sitio gratis y descubre qué te falta en menos de 30 segundos.
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors"
              >
                Escanear mi sitio gratis
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Next/Prev */}
          {(prev || next) && (
            <nav className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-200 pt-10">
              {prev ? (
                <Link
                  to={`/blog/${prev.slug}`}
                  className="group p-5 rounded-2xl border border-slate-200 hover:border-slate-900 transition-colors"
                >
                  <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
                    ← Anterior
                  </span>
                  <h4 className="mt-2 font-display text-base text-slate-900 group-hover:text-blue-700 line-clamp-2">
                    {prev.title}
                  </h4>
                </Link>
              ) : (
                <div />
              )}
              {next && (
                <Link
                  to={`/blog/${next.slug}`}
                  className="group p-5 rounded-2xl border border-slate-200 hover:border-slate-900 transition-colors text-right"
                >
                  <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
                    Siguiente →
                  </span>
                  <h4 className="mt-2 font-display text-base text-slate-900 group-hover:text-blue-700 line-clamp-2">
                    {next.title}
                  </h4>
                </Link>
              )}
            </nav>
          )}
        </div>
      </article>
    </>
  );
}
